import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ChefHat } from "lucide-react";
import { api } from "@/lib/api";

export default function TakeawaySuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [orderNo, setOrderNo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (stop) return;
        if (data.payment_status === "paid") {
          setOrderNo(data.order_number);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep trying */ }
      tries.current += 1;
      if (tries.current > 15) { setState("failed"); return; }
      if (!stop) setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-background border-2 border-foreground rounded-3xl shadow-brutal-lg p-8 text-center" data-testid="takeaway-success">
        {state === "checking" && (
          <>
            <Loader2 size={40} className="animate-spin mx-auto text-secondary" />
            <h1 className="font-head font-extrabold text-2xl mt-4">Confirming your payment…</h1>
            <p className="text-foreground/60 mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-secondary" />
            <h1 className="font-head font-extrabold text-3xl mt-4">Order confirmed!</h1>
            {orderNo && <p className="font-mono-plex text-lg mt-2">Order #<span data-testid="order-number" className="font-bold">{orderNo}</span></p>}
            <div className="flex items-center justify-center gap-2 text-foreground/70 mt-4 bg-muted border-2 border-foreground/15 rounded-xl py-3">
              <ChefHat size={18} /> Sent to the kitchen — we're on it.
            </div>
            <Link to="/" data-testid="success-home" className="inline-block mt-6 font-head font-bold border-2 border-foreground rounded-full px-6 py-2.5 hover:bg-muted">Back to Harbour Haze</Link>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle size={48} className="mx-auto text-destructive" />
            <h1 className="font-head font-extrabold text-2xl mt-4">We couldn't confirm your order</h1>
            <p className="text-foreground/60 mt-2">If you were charged, please contact us and we'll sort it out.</p>
            <Link to="/" data-testid="failed-home" className="inline-block mt-6 font-head font-bold border-2 border-foreground rounded-full px-6 py-2.5 hover:bg-muted">Back to Harbour Haze</Link>
          </>
        )}
      </div>
    </div>
  );
}
